/**
 * TanStack Query hooks for the /downloads page (STD-015/016 — components go
 * through these hooks, never apiService directly).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { publishRelease } from './publishRelease';
import type {
  CreateProductInput,
  PublishReleaseInput,
  SoftwareRelease,
  UpdateProductInput,
} from '../../types/downloads';

export const downloadsKeys = {
  all: () => ['software-releases'] as const,
  list: () => ['software-releases', 'list'] as const,
  products: () => ['software-releases', 'products'] as const,
};

export function useSoftwareReleasesQuery(enabled = true) {
  return useQuery({
    queryKey: downloadsKeys.list(),
    queryFn: async () => (await apiService.getSoftwareReleases()).releases,
    meta: { errorMessage: 'Failed to load software releases' },
    enabled,
  });
}

/** Issue a presigned download link (also records the audit event server-side). */
export function useDownloadReleaseMutation() {
  return useMutation({
    mutationFn: (releaseId: string) => apiService.requestSoftwareReleaseDownload(releaseId),
  });
}

export interface PublishReleaseVariables {
  input: PublishReleaseInput;
  file: File;
  onProgress?: (percent: number) => void;
}

/**
 * Full publish flow: upload the bytes through the backend (with progress) →
 * finalize (backend HEADs the object, streams a SHA256, writes the catalog row).
 *
 * Two calls, not one, on purpose: finalize is the verification leg and stays
 * byte-identical to what the CLI/script publish path has always used. The
 * upload leg was a direct-to-S3 PUT until it turned out a browser can't make
 * that request at all (`docs/GOTCHAS.md` G-7).
 */
export function usePublishReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation<SoftwareRelease, Error, PublishReleaseVariables>({
    mutationFn: ({ input, file, onProgress }) =>
      publishRelease(apiService, input, file, onProgress),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: downloadsKeys.all() });
    },
  });
}

export function useDeleteReleaseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => apiService.deleteSoftwareRelease(releaseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: downloadsKeys.all() });
    },
  });
}

/**
 * The product catalog — XER-admin only, so `enabled` is passed by the caller
 * rather than left to default true: a non-admin would get a 403 and an error
 * toast for a request it was never supposed to make (the `useShareCounterparties`
 * precedent in the Files tab).
 */
export function useSoftwareProductsQuery(enabled = true) {
  return useQuery({
    queryKey: downloadsKeys.products(),
    queryFn: async () => (await apiService.getSoftwareProducts()).products,
    meta: { errorMessage: 'Failed to load software products' },
    enabled,
  });
}

export function useCreateProductMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => apiService.createSoftwareProduct(input),
    onSuccess: () => {
      // Invalidate the whole namespace, not just the product list: a new
      // product changes what /downloads can label and order.
      void queryClient.invalidateQueries({ queryKey: downloadsKeys.all() });
    },
  });
}

export function useUpdateProductMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: UpdateProductInput }) =>
      apiService.updateSoftwareProduct(key, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: downloadsKeys.all() });
    },
  });
}
